import React, { useEffect, useState } from 'react';
import { FieldArray, FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import QueryString from 'qs';
import { toast } from 'react-toastify';
import PageWithCard from '../../components/infrastructure/PageWithCard';
import PrimaryButton from '../../components/infrastructure/Buttons/PrimaryButton';
import DangerButton from '../../components/infrastructure/Buttons/DangerButton';
import FormikInputGroup from '../../components/formik/FormikInputGroup';
import FormikAsyncSelect from '../../components/formik/FormikAsyncSelect';
import FormikSelectGroup from '../../components/formik/FormikSelectGroup';
import FormikDirectImageUpload from '../../components/formik/FormikDirectImageUpload';
import FormikDirectFileUpload from '../../components/formik/FormikDirectFileUpload';
import FormikInputDateGroup from '../../components/formik/FormikInputDateGroup';
import FormikTextareaGroup from '../../components/formik/FormikTextareaGroup';
import { authAxiosInstance } from '../../utils/axiosConfig';
import { getAuth } from '../../app/reducers/Auth/authSlice';
import {
    fetchEmployees,
    getEmployees,
} from '../../app/reducers/Users/EmployeeSlice';
import { ClipLoader } from 'react-spinners';
import {
    MATERIAL_CONDITION_OPTIONS,
    SHIFTS_OPTIONS,
    MACHINE_WARRANTY_OPTIONS,
    COMPETITOR_APPROACH,
} from '../../utils/dropdownOptions';
import { generateEmployeeOptions } from '../../utils/Utils';

const EditVisitForm = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const { user } = useSelector(getAuth);
    const { employees } = useSelector(getEmployees);
    const navigate = useNavigate();
    const [visitFormData, setVisitFormData] = useState(null);
    const [fetching, setFetching] = useState(false);

    const fetchVisitForm = async () => {
        try {
            setFetching(true);
            const string = QueryString.stringify({ _id: id, populate: true });
            const response = await authAxiosInstance.get(`/visitForm?${string}`);
            const data = response?.data?.data?.docs?.[0];
            if (data) {
                setVisitFormData(data);
            }
        } catch (error) {
            console.log(error);
            toast.error('Failed to fetch visit form');
        } finally {
            setFetching(false);
        }
    };

    useEffect(() => {
        fetchVisitForm();
    }, [id]);

    useEffect(() => {
        dispatch(fetchEmployees({ limit: 500 }));
    }, []);

    const computeInitialValues = (data) => {
        if (!data) {
            return {
                company: '',
                date: '',
                visitForm_no: '',
                createdBy: '',
                creatorName: '',
                materialCondition: '',
                isOtherMaterialCondition: false,
                shifts: '',
                isOtherShifts: false,
                competitorInkApproach: '',
                whichCompetitorInk: '',
                competitorMachineApproach: '',
                whichCompetitorMachine: '',
                resolution: '',
                photos: [],
                videos: [],
            };
        }

        const matOptions = MATERIAL_CONDITION_OPTIONS.map((o) => o.value);
        const shiftOptions = SHIFTS_OPTIONS.map((o) => o.value);
        const warrantyOptions = MACHINE_WARRANTY_OPTIONS.map((o) => o.value);

        return {
            company: data?.company?._id || '',
            date: data?.date || '',
            visitForm_no: data?.visitForm_no || '',
            createdBy: data?.createdBy?._id || '',
            creatorName: data?.creatorName || `${data?.createdBy?.firstName || ''} ${data?.createdBy?.lastName || ''}`.trim(),
            materialCondition: data?.materialCondition || '',
            isOtherMaterialCondition: data?.materialCondition ? !matOptions.includes(data.materialCondition) : false,
            shifts: data?.shifts || '',
            isOtherShifts: data?.shifts ? !shiftOptions.includes(data.shifts) : false,
            competitorInkApproach: data?.competitorInkApproach || '',
            whichCompetitorInk: data?.whichCompetitorInk || '',
            competitorMachineApproach: data?.competitorMachineApproach || '',
            whichCompetitorMachine: data?.whichCompetitorMachine || '',
            resolution: data?.resolution || '',
            photos: data?.photos || [],
            videos: data?.videos || [],
        };
    };

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: computeInitialValues(visitFormData),
        validationSchema: Yup.object({
            date: Yup.string().required('Date is required'),
            company: Yup.string().required('Company is required'),
            createdBy: Yup.string().required('Created By is required'),
            shifts: Yup.string().required('Shifts is required'),
            materialCondition: Yup.string().required(
                'Material Condition is required'
            ),
            competitorInkApproach: Yup.string().required(
                'Competitor Ink Approach is required'
            ),
            competitorMachineApproach: Yup.string().required(
                'Competitor Machine Approach is required'
            ),
            whichCompetitorInk: Yup.string().when(
                'competitorInkApproach',
                {
                    is: 'yes',
                    then: (schema) =>
                        schema.required('Which Competitor Ink is required'),
                    otherwise: (schema) => schema.notRequired(),
                }
            ),
            whichCompetitorMachine: Yup.string().when(
                'competitorMachineApproach',
                {
                    is: 'yes',
                    then: (schema) =>
                        schema.required('Which Competitor Machine is required'),
                    otherwise: (schema) => schema.notRequired(),
                }
            ),
        }),
        onSubmit: async (values) => {
            try {
                const {
                    isOtherMaterialCondition,
                    isOtherShifts,
                    isOtherMachineWarranty,
                    ...payload
                } = values;
                await authAxiosInstance.patch('/visitForm', { _id: id, ...payload });
                toast.success('Visit form updated');
                navigate('/visitForm/view-visit-form');
            } catch (error) {
                if (error?.response?.data?.error?.message) {
                    toast.error(error.response.data.error.message);
                } else {
                    toast.error('Failed to update visit form');
                }
                console.log(error);
            }
        },
    });

    if (fetching) {
        return (
            <PageWithCard heading="Edit Visit Form">
                <div className="flex justify-center p-8">
                    <ClipLoader />
                </div>
            </PageWithCard>
        );
    }

    return (
        <PageWithCard heading="Edit Visit Form">
            <form
                onSubmit={formik.handleSubmit}
                className="flex flex-col gap-4"
            >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormikInputDateGroup
                        label="Date"
                        name="date"
                        formik={formik}
                        showTimeSelect
                        timeFormat="HH:mm"
                        timeIntervals={15}
                        dateFormat="dd/MM/yyyy HH:mm"
                        required
                    />

                    <FormikInputGroup
                        label="Visit Form No"
                        name="visitForm_no"
                        formik={formik}
                        readOnly
                    />
                </div>

                <FormikSelectGroup
                    label="Created By"
                    name="createdBy"
                    formik={formik}
                    options={generateEmployeeOptions(employees?.docs)}
                    required
                    isDisabled={!['sup_admin', 'admin', 'local_admin'].includes(user?.role)}
                    onChange={(selectedOption) => {
                        formik.setFieldValue('createdBy', selectedOption.value);
                        formik.setFieldValue('creatorName', selectedOption.label);
                    }}
                />

                <FormikAsyncSelect
                    label="Select Company"
                    name="company"
                    formik={formik}
                    getOptions={async (value) => {
                        try {
                            const payload = {
                                search: value,
                                approvedStatus: 'approved',
                            };

                            const string = QueryString.stringify(payload);

                            const response = await authAxiosInstance.get(
                                `/company?${string}`
                            );

                            return (
                                response?.data?.data?.docs?.map((ele) => ({
                                    label: ele.name,
                                    value: ele._id,
                                })) || []
                            );
                        } catch (error) {
                            console.log(error);
                            return [];
                        }
                    }}
                    onChange={(selectedOption) => {
                        formik.setFieldValue('company', selectedOption.value);
                    }}
                    required
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                    {formik.values.isOtherMaterialCondition ? (
                        <div>
                            <FormikInputGroup
                                name="materialCondition"
                                label="Material Condition"
                                formik={formik}
                                required
                            />

                            <p
                                className="text-blue-600 text-sm cursor-pointer underline mt-1"
                                onClick={() => {
                                    formik.setFieldValue('materialCondition', '');
                                    formik.setFieldValue(
                                        'isOtherMaterialCondition',
                                        false
                                    );
                                }}
                            >
                                Select from list
                            </p>
                        </div>
                    ) : (
                        <FormikSelectGroup
                            label="Material Condition"
                            name="materialCondition"
                            formik={formik}
                            options={MATERIAL_CONDITION_OPTIONS}
                            onChange={(selectedOption) => {
                                if (selectedOption?.value === 'other') {
                                    formik.setFieldValue('materialCondition', '');
                                    formik.setFieldValue(
                                        'isOtherMaterialCondition',
                                        true
                                    );
                                } else {
                                    formik.setFieldValue(
                                        'materialCondition',
                                        selectedOption?.value
                                    );

                                    formik.setFieldValue(
                                        'isOtherMaterialCondition',
                                        false
                                    );
                                }
                            }}
                            required
                        />
                    )}

                    {formik.values.isOtherShifts ? (
                        <div>
                            <FormikInputGroup
                                name="shifts"
                                label="Shifts"
                                formik={formik}
                                required
                            />

                            <p
                                className="text-blue-600 text-sm cursor-pointer underline mt-1"
                                onClick={() => {
                                    formik.setFieldValue('shifts', '');
                                    formik.setFieldValue('isOtherShifts', false);
                                }}
                            >
                                Select from list
                            </p>
                        </div>
                    ) : (
                        <FormikSelectGroup
                            label="Shifts"
                            name="shifts"
                            formik={formik}
                            options={SHIFTS_OPTIONS}
                            onChange={(selectedOption) => {
                                if (selectedOption?.value === 'other') {
                                    formik.setFieldValue('shifts', '');
                                    formik.setFieldValue('isOtherShifts', true);
                                } else {
                                    formik.setFieldValue(
                                        'shifts',
                                        selectedOption?.value
                                    );

                                    formik.setFieldValue('isOtherShifts', false);
                                }
                            }}
                            required
                        />
                    )}
                </div>

                {/* Competitor Ink */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                    <FormikSelectGroup
                        label="Competitor Ink Approach"
                        name="competitorInkApproach"
                        formik={formik}
                        options={COMPETITOR_APPROACH}
                        required
                    />

                    {formik?.values?.competitorInkApproach === 'yes' ? (
                        <FormikInputGroup
                            label="Which Competitor Ink"
                            name="whichCompetitorInk"
                            formik={formik}
                            required
                        />
                    ) : (
                        <div />
                    )}
                </div>

                {/* Competitor Machine */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                    <FormikSelectGroup
                        label="Competitor Machine Approach"
                        name="competitorMachineApproach"
                        formik={formik}
                        options={COMPETITOR_APPROACH}
                        required
                    />

                    {formik?.values?.competitorMachineApproach === 'yes' ? (
                        <FormikInputGroup
                            label="Which Competitor Machine"
                            name="whichCompetitorMachine"
                            formik={formik}
                            required
                        />
                    ) : (
                        <div />
                    )}
                </div>

                <FormikTextareaGroup
                    label="Remark"
                    name="resolution"
                    formik={formik}
                />

                {/* Photos */}
                <div className="border rounded-lg p-4 bg-slate-50">
                    <h3 className="text-sm font-semibold text-slate-700 mb-3">
                        Photos
                    </h3>

                    <FormikProvider value={formik}>
                        <FieldArray
                            name="photos"
                            render={(arrayHelpers) => {
                                return (
                                    <div className="flex flex-col gap-3">

                                        {formik.values.photos.map((ele, i) => (
                                            <div
                                                className="flex items-center gap-3 bg-white p-3 rounded border"
                                                key={i}
                                            >
                                                <div className="flex-1">
                                                    <FormikDirectImageUpload
                                                        label={`Photo ${i + 1}`}
                                                        formik={formik}
                                                        name={`photos.${i}`}
                                                        randomize
                                                        location={'iml_group/visitForm/'}
                                                    />
                                                </div>

                                                <DangerButton
                                                    onClick={() => {
                                                        arrayHelpers.remove(i);
                                                    }}
                                                >
                                                    Remove
                                                </DangerButton>
                                            </div>
                                        ))}

                                        <div>
                                            <PrimaryButton
                                                onClick={() => {
                                                    arrayHelpers.push('');
                                                }}
                                            >
                                                Add Photo
                                            </PrimaryButton>
                                        </div>
                                    </div>
                                );
                            }}
                        />
                    </FormikProvider>
                </div>

                {/* Videos */}
                <div className="border rounded-lg p-4 bg-slate-50">
                    <h3 className="text-sm font-semibold text-slate-700 mb-3">
                        Videos
                    </h3>

                    <FormikProvider value={formik}>
                        <FieldArray
                            name="videos"
                            render={(arrayHelpers) => {
                                return (
                                    <div className="flex flex-col gap-3">

                                        {formik.values.videos.map((ele, i) => (
                                            <div
                                                className="flex items-center gap-3 bg-white p-3 rounded border"
                                                key={i}
                                            >
                                                <div className="flex-1">
                                                    <FormikDirectFileUpload
                                                        label={`Video ${i + 1}`}
                                                        formik={formik}
                                                        name={`videos.${i}`}
                                                        randomize
                                                        location={'iml_group/visitForm/'}
                                                    />
                                                </div>

                                                <DangerButton
                                                    onClick={() => {
                                                        arrayHelpers.remove(i);
                                                    }}
                                                >
                                                    Remove
                                                </DangerButton>
                                            </div>
                                        ))}

                                        <div>
                                            <PrimaryButton
                                                onClick={() => {
                                                    arrayHelpers.push('');
                                                }}
                                            >
                                                Add Video
                                            </PrimaryButton>
                                        </div>
                                    </div>
                                );
                            }}
                        />
                    </FormikProvider>
                </div>

                <div className="pt-2">
                    {formik.isSubmitting ? (
                        <ClipLoader />
                    ) : (
                        <PrimaryButton type="submit">
                            Submit
                        </PrimaryButton>
                    )}
                </div>
            </form>
        </PageWithCard>
    );
};

export default EditVisitForm;
